import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import styles from './StripePaymentForm.module.css';

interface StripePaymentFormProps {
    clientSecret: string;
    onSuccess: (paymentIntentId: string) => void;
    onError: (message: string) => void;
    submitLabel?: string;
    disabled?: boolean;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
    clientSecret,
    onSuccess,
    onError,
    submitLabel = 'Pay Now',
    disabled = false,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    const handleCardChange = (event: StripeCardElementChangeEvent) => {
        setCardError(event.error ? event.error.message : null);
        setCardComplete(event.complete);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements || !cardComplete) return;

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) return;

        setProcessing(true);
        setCardError(null);

        try {
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: { card: cardElement },
            });

            if (error) {
                setCardError(error.message ?? 'Payment failed. Please try again.');
                onError(error.message ?? 'Payment failed. Please try again.');
            } else if (paymentIntent?.status === 'succeeded') {
                onSuccess(paymentIntent.id);
            } else {
                onError(`Unexpected payment status: ${paymentIntent?.status}`);
            }
        } catch {
            const msg = 'An unexpected error occurred. Please try again.';
            setCardError(msg);
            onError(msg);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.cardElementWrapper}>
                <label className={styles.cardLabel}>Card details</label>
                <div className={styles.cardElementContainer}>
                    <CardElement
                        onChange={handleCardChange}
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#1a1a2e',
                                    fontFamily: 'inherit',
                                    '::placeholder': { color: '#a0a0b0' },
                                },
                                invalid: { color: '#e52d42' },
                            },
                            hidePostalCode: false,
                        }}
                    />
                </div>
                {cardError && <p className={styles.cardError}>{cardError}</p>}
            </div>

            <button
                type="submit"
                className={styles.submitButton}
                disabled={disabled || processing || !stripe || !cardComplete}
            >
                {processing ? 'Processing...' : submitLabel}
            </button>
        </form>
    );
};

export default StripePaymentForm;
